// Tab-key helpers + the ActiveTab bridge + the nascent-conversation seed.
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition). Pure: no
// I/O, no React. The lowest substrate layer above `types.ts` — every leaf/tree
// op reaches for `keyOf` / `sameKey`.

import { exhaustSwitchCase } from "@bilda/utils";
import type { ActiveTab } from "../types.js";
import { nextConversationId } from "../types.js";
import type { ConversationTab, Tab, TabKey } from "./types.js";

// === Tab-key helpers (pure) ==================================================

export const keyOf = (tab: Tab): TabKey => {
    switch (tab.type) {
        case "conversation":
            return { type: "conversation", id: tab.id };
        case "settings":
            return { type: "settings" };
        case "home":
            return { type: "home" };
        case "studio":
            return { type: "studio" };
        case "projects":
            return { type: "projects" };
        case "knowledge":
            return { type: "knowledge" };
        case "search":
            return { type: "search" };
        case "resource":
            return { type: "resource", id: tab.resource.id };
        case "quality":
            return { type: "quality", materialId: tab.materialId };
        default:
            return exhaustSwitchCase(tab);
    }
};

export const sameKey = (a: TabKey, b: TabKey): boolean => {
    if (a.type !== b.type) return false;
    if (a.type === "resource" && b.type === "resource") return a.id === b.id;
    if (a.type === "conversation" && b.type === "conversation")
        return a.id === b.id;
    if (a.type === "quality" && b.type === "quality")
        return a.materialId === b.materialId;
    return true;
};

export const tabHasKey = (tab: Tab, key: TabKey): boolean =>
    sameKey(keyOf(tab), key);

// A stable string form of a key, for de-duplication sets on restore.
export const keyStr = (k: TabKey): string =>
    k.type === "resource"
        ? `resource:${k.id}`
        : k.type === "quality"
          ? `quality:${k.materialId}`
          : k.type === "conversation"
            ? `conversation:${k.id}`
            : k.type;

// === ActiveTab ⇄ TabKey (types.ts's ActiveTab is member-for-member a TabKey) ==

export const activeToKey = (a: ActiveTab): TabKey => {
    switch (a.type) {
        case "conversation":
            return { type: "conversation", id: a.id };
        case "settings":
            return { type: "settings" };
        case "home":
            return { type: "home" };
        case "studio":
            return { type: "studio" };
        case "projects":
            return { type: "projects" };
        case "knowledge":
            return { type: "knowledge" };
        case "search":
            return { type: "search" };
        case "resource":
            return { type: "resource", id: a.id };
        case "quality":
            return { type: "quality", materialId: a.materialId };
        default:
            return exhaustSwitchCase(a);
    }
};

export const keyToActive = (k: TabKey): ActiveTab => {
    switch (k.type) {
        case "conversation":
            return { type: "conversation", id: k.id };
        case "settings":
            return { type: "settings" };
        case "home":
            return { type: "home" };
        case "studio":
            return { type: "studio" };
        case "projects":
            return { type: "projects" };
        case "knowledge":
            return { type: "knowledge" };
        case "search":
            return { type: "search" };
        case "resource":
            return { type: "resource", id: k.id };
        case "quality":
            return { type: "quality", materialId: k.materialId };
        default:
            return exhaustSwitchCase(k);
    }
};

// A fresh nascent conversation tab (the seed of any otherwise-empty leaf). Its
// id is the deterministic structural seed (`nextConversationId([])`) so the
// never-visited tree is reproducible; the explicit ⌘T/"+" gestures mint a UUID
// in the shell (types.ts `nextConversationId` header).
export const seedConversationTab = (): ConversationTab => ({
    type: "conversation",
    id: nextConversationId([]),
});
